import { Injectable, signal } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { v5 } from "uuid";
import { AuthCredentials } from "../types/Auth/Auth";
import { EnvironmentService } from "./environment.service";
import { handlePromise } from "../../utils";
import { AuthRepository } from "../repositories/auth.repository";
import { UsersRepository } from "../repositories/users.repository";
import { Client } from "../types/Client/Client";
import { ClientService } from "./client.service";
import { RequestService } from "./request.service";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { NetworkService } from "./network.service";
import { LoginResponse } from "../types/RequestResponses/LoginResponse";

@Injectable({
  providedIn: "root",
})
export class AuthService extends BaseService {
  currentAuth = signal<AuthCredentials>(null as any);

  constructor(
    private http: HttpClient,
    private clientService: ClientService,
    private authRepo: AuthRepository,
    private authUsersRepo: UsersRepository,
    private router: Router,
    private environmentService: EnvironmentService,
    private requestService: RequestService,
    private networkService: NetworkService,
    logService: LogService,
  ) {
    super(logService, "AuthService");
    this.authRepo.get().then(
      (auth) => {
        if (auth) {
          this.currentAuth.set(auth);
        }
      },
      (err) => this.logger.error(err),
    );
  }

  public async getAuth(): Promise<AuthCredentials> {
    return this.currentAuth() || (await this.authRepo.get());
  }

  public async setAuth(
    credentials: AuthCredentials,
    overwrite = false,
  ): Promise<AuthCredentials> {
    const auth = overwrite ? {} : await this.getAuth();
    const mergedAuth = { ...auth, ...credentials };
    this.currentAuth.set(mergedAuth);
    if (mergedAuth.userID) {
      await this.authUsersRepo.saveUser(mergedAuth);
    }
    return this.authRepo.set(mergedAuth);
  }

  public async userIsAuthenticated(user?: AuthCredentials): Promise<boolean> {
    const isOnline = this.networkService.getCurrentStatus();
    if (!isOnline && user) {
      return true;
    }
    const [err, auth] = await handlePromise(this.getAuth());
    if (err) {
      this.logger.error(err);
    }
    if (!isOnline && auth?.sessionID) {
      return true;
    }
    if (isOnline && !auth?.sessionID) {
      return false;
    }
    return this.validateSession();
  }

  public async validateSession(): Promise<boolean> {
    const [err] = await handlePromise(
      this.requestService.post(
        "login/authcheck",
        undefined,
        false,
        true,
        (error: any) => error.status === 401 || error.status === 0,
      ),
    );
    if (err) {
      return false;
    }
    return true;
  }

  public async getEnvironment(): Promise<string> {
    const auth = await this.getAuth();
    return auth.env;
  }

  public comparePasswords(
    input: string,
    hashed: string,
    userID: string,
  ): boolean {
    return this.simpleHash(userID, input) === hashed;
  }

  private async getOfflineUser(
    credentials: Partial<AuthCredentials>,
    environment: string,
  ): Promise<AuthCredentials | undefined> {
    if (!credentials.password || !credentials.email) {
      throw new Error("Missing Required Inputs");
    }

    const user = await this.authUsersRepo.getUserByEmail(
      credentials.email,
      environment,
    );
    if (!user) {
      return undefined;
    }
    const passwordsMatch = this.comparePasswords(
      credentials.password,
      user.password,
      user.userID,
    );

    if (!passwordsMatch) {
      throw new Error("Incorrect Password");
    }
    return user;
  }

  private async offlineLogin(
    credentials: Partial<AuthCredentials>,
    environment: string,
  ): Promise<Client[]> {
    const user = await this.getOfflineUser(credentials, environment);
    if (user) {
      const isAuth = await this.userIsAuthenticated(user);
      if (!isAuth) {
        throw new Error("Offline User Issue");
      }
      await this.setAuth(user);
      await this.requestService.setAuth(user);
      return this.clientService.getDownloadedClientsByAuth(user);
    }
    throw new Error("Offline User Not Found");
  }

  private simpleHash(userID: string, value: string = "admin"): string {
    return v5(value, userID);
  }

  private async networkLogin(
    credentials: Partial<AuthCredentials>,
    environment: string,
    authID?: string,
  ): Promise<Client[]> {
    if (
      !authID &&
      (!credentials.email || !credentials.password || !environment)
    ) {
      throw new Error("Missing Required Inputs");
    }

    const { availableClients, sessionID, userID, mapKey, email } =
      await this.makeLoginRequest(credentials, environment, authID);
    const auth = {
      email,
      password: this.simpleHash(userID, credentials.password),
      env: environment,
      userID,
      sessionID,
      mapKey,
    };
    await this.setAuth(auth);
    await this.clientService.setAvailableClients(availableClients);
    await this.clientService.clearCurrentClient();
    await this.requestService.setAuth(auth);
    return availableClients;
  }

  private handleLoginResponseError(res: LoginResponse) {
    if (!res.data || res.response === "Error") {
      this.logger.error("Bad Response", res);
      throw new Error("Bad Response");
    }
    if (!res.data.length) {
      this.logger.warn("No Clients Found", res);
      throw new Error("No Clients Found");
    }
  }

  private async makeLoginRequest(
    credentials: Partial<AuthCredentials>,
    environment: string,
    authID?: string,
  ): Promise<{
    mapKey: string;
    sessionID: string;
    userID: string;
    email: string;
    availableClients: Client[];
  }> {
    const url = this.environmentService.getAdminPortalUrl(environment);
    const res: LoginResponse = await lastValueFrom(
      this.http.post<LoginResponse>(`${url}login`, {
        email: credentials.email,
        password: credentials.password,
        auth: authID,
      }),
    );
    const { data, sessionID, userID, mapKey, email } = res;
    this.handleLoginResponseError(res);
    return {
      sessionID,
      mapKey,
      email,
      userID,
      availableClients: data,
    };
  }

  public login(
    credentials: Partial<AuthCredentials>,
    environment: string,
    authID?: string,
  ): Promise<[Error | undefined, Client[] | undefined]> {
    const status = this.networkService.getCurrentStatus();
    const isOnline = status;
    if (isOnline) {
      return handlePromise(this.networkLogin(credentials, environment, authID));
    }
    return handlePromise(this.offlineLogin(credentials, environment));
  }

  public async logout(): Promise<boolean> {
    const auth = await this.getAuth();
    await this.setAuth({ email: auth.email, env: auth.env } as any, true);
    await this.requestService.clearRequestData();
    await this.clientService.clearCurrentClient();
    await this.clientService.clearAvailableClients();
    return this.router.navigate(["/login"]);
  }
}
